import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { PageFlip } from 'page-flip';
import PagePreviewViewer from '../../common/PagePreviewViewer';
import { getPageMetrics } from './atelierLayoutGeometry';
import { gutterSafetyMmOf } from '../../../utils/printFormats';
import SpreadPhotoInfo from '../../common/SpreadPhotoInfo';
import { FORMAT_DIMENSIONS_MM } from './photoQuality';
import { CoverPhotoOverlay } from './AtelierBookView';
import CoverTextOverlay from './CoverTextOverlay';
import CoverMultiPhotoOverlay, { isMultiPhotoCover } from './CoverMultiPhotoOverlay';
import BookipixLoader from './BookipixLoader';
import {
  FLIP_COVER_INDEX,
  buildFlipEntries,
  flipIndexFromContentPage,
  flipBackCoverIndex,
  contentPageFromFlipIndex
} from '../../../utils/flipBookGeometry';

// Atelier, feuilletage REEL (retour utilisateur, 2026-10-11 : "si on peut
// feuilletter le rendu comme dans Exemples ça serait top ! (atelier et
// aperçu)") — meme bibliotheque et meme schema d'integration IMPERATIF que
// BookPreviewFinalLuxe.js (voir son en-tete) et ExemplesLuxe.js : page-flip
// prend possession directe du DOM de ses pages, aucun composant React ne
// doit vivre a l'INTERIEUR du conteneur qu'on lui passe.
//
// Difference majeure avec l'Apercu final (lecture seule) : ici il faut
// continuer a EDITER une page (glisser-deposer une photo, cliquer un
// emplacement, taper du texte...) alors qu'elle est geree par page-flip.
// Solution validee par un prototype isole (depuis retire) avant d'ecrire ce fichier :
// un <div> "hote" cree AU MEME MOMENT que chaque page (donc
// connu de page-flip comme un enfant ordinaire qu'il deplace/anime avec le
// reste, jamais recree par lui) recoit un VRAI composant React (la vraie
// AtelierPageOverlay/CoverPhotoOverlay, inchangee, avec tous ses hooks) via
// createPortal — React continue de gerer ce noeud normalement (re-rendus,
// evenements, state) pendant que page-flip anime son parent autour.
//
// AtelierBookView.js (l'ancienne vue, sans feuilletage) reste intact et
// continue d'exister tel quel : filet de secours en un clic (changer un
// seul import dans BookAtelierLuxe.js) si ce feuilletage devait etre
// desactive. Seul CoverPhotoOverlay en est reexporte (voir plus bas),
// jamais duplique.
//
// L'INTEGRALITE du livre est montee en DOM des le premier rendu (toutes les
// pages, meme non visibles) : page-flip a besoin de connaitre toutes ses
// pages pour animer les feuillets qui ne sont pas directement adjacents a
// la page courante. Le CONTENU (iframe.srcdoc) de chaque page, lui, est
// injecte/mis a jour APRES coup (voir l'effet de "hot-swap" plus bas) sans
// jamais reconstruire l'instance PageFlip — une page qui vient d'etre
// sauvegardee (autosave) doit se mettre a jour sans perdre la position de
// lecture ni une animation en cours.

const PX_PER_MM = 96 / 25.4;
const SPREAD_GAP_PX = 16;

function EyeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1.5 12S5 5 12 5s10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

// PHOTO INTROUVABLE (fichier supprime du stockage, lien casse) : sans ca, la
// page affichait une image cassee du navigateur. On pose un cadre gris
// "Photo introuvable" DANS L'APERCU de l'atelier uniquement — le rendu servi
// au serveur d'impression n'est jamais modifie (un fichier manquant y reste
// signale par le controle qualite). Le bouton "Remplacer" est celui, deja
// present, du cadre (AtelierPageOverlay : "Changer la photo").
function flagMissingPhotos(iframe, label) {
  let doc = null;
  try { doc = iframe.contentDocument; } catch (_error) { return; }
  if (!doc || !doc.head) return;
  if (!doc.getElementById('atelier-missing-style')) {
    const style = doc.createElement('style');
    style.id = 'atelier-missing-style';
    style.textContent = '.photo-frame.is-missing{background:#ebecf5;display:flex;align-items:center;justify-content:center}'
      + '.photo-frame.is-missing img{display:none}'
      + '.photo-frame.is-missing::after{content:attr(data-missing);font:600 13px sans-serif;color:#5d6086;padding:8px;text-align:center}';
    doc.head.appendChild(style);
  }
  doc.querySelectorAll('.photo-frame img').forEach((img) => {
    const mark = () => {
      const frame = img.closest('.photo-frame');
      if (!frame) return;
      frame.classList.add('is-missing');
      frame.setAttribute('data-missing', label);
    };
    if (img.complete && img.naturalWidth === 0 && img.getAttribute('src')) mark();
    else img.addEventListener('error', mark, { once: true });
  });
}

// --- Plateau de feuilletage (page-flip), IMPERATIF ------------------------
//
// Construit UNE SEULE FOIS par [totalPages, printFormat] (ajouter/retirer
// des pages ou changer de format recree le livre ; tout le reste — contenu,
// page selectionnee — se met a jour EN PLACE, voir les effets suivants).
// `onMountsReady`/`onFlipChange`/`apiRef`/`onPageClick` traversent la
// frontiere imperatif <-> React : mêmes refs-pont (jamais l'etat directement
// dans l'effet de montage) que ExemplesLuxe.js/BookPreviewFinalLuxe.js et le
// prototype — voir leurs en-tetes pour la raison (fermeture figee au premier
// rendu sinon).
function AtelierFlipStage({
  totalPages, printFormat, currentPageIndex, labels,
  contentRef, onMountsReady, onFlipChange, apiRef, onPageClick
}) {
  const containerRef = useRef(null);
  const onPageClickRef = useRef(onPageClick);
  onPageClickRef.current = onPageClick;
  const onFlipChangeRef = useRef(onFlipChange);
  onFlipChangeRef.current = onFlipChange;
  const onMountsReadyRef = useRef(onMountsReady);
  onMountsReadyRef.current = onMountsReady;
  const iframeMapRef = useRef({ interior: {} });
  const labelsRef = useRef(labels);
  labelsRef.current = labels;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    container.innerHTML = '';
    const book = document.createElement('div');
    const entries = buildFlipEntries(totalPages);
    const resizeObservers = [];
    const mounts = [];
    const iframeMap = { interior: {} };

    const formatDims = FORMAT_DIMENSIONS_MM[printFormat] || FORMAT_DIMENSIONS_MM.standard;
    const naturalWidthPx = formatDims.widthMm * PX_PER_MM;
    const naturalHeightPx = formatDims.heightMm * PX_PER_MM;
    const content = contentRef.current;
    // Rapport largeur/hauteur d'une DOUBLE page, lu par le CSS pour borner la
    // largeur du livre selon la hauteur disponible (voir .atelier-flip-stage).
    container.style.setProperty('--flip-spread-ratio', String((2 * naturalWidthPx) / naturalHeightPx));

    entries.forEach((entry, flipIndex) => {
      const pageEl = document.createElement('div');
      const isContreplat = entry.kind === 'contreplat';
      pageEl.className = [
        'atelier-flip-page',
        'atelier-page-pane',
        isContreplat ? 'is-contreplat' : '',
        entry.kind === 'interior' ? 'is-selectable' : ''
      ].filter(Boolean).join(' ');
      pageEl.dataset.kind = entry.kind;
      if (entry.kind === 'interior') pageEl.dataset.pageIndex = String(entry.pageIndex);

      if (isContreplat) {
        const label = document.createElement('span');
        label.className = 'atelier-page-pane-label';
        // Le PREMIER contre-plat de la sequence fait face a la couverture,
        // le DERNIER a la 4e — voir utils/flipBookGeometry.js (buildFlipEntries).
        label.textContent = flipIndex === 1 ? labels.insideCover : labels.insideBackCover;
        pageEl.appendChild(label);
      } else {
        const html = entry.kind === 'cover' ? content.coverHtml
          : entry.kind === 'back-cover' ? content.backCoverHtml
            : content.pagePreviewCache[entry.pageIndex];

        const clip = document.createElement('div');
        clip.className = 'atelier-flip-page-clip';
        const iframe = document.createElement('iframe');
        iframe.title = entry.kind === 'interior' ? `page-${entry.pageIndex}` : entry.kind;
        iframe.srcdoc = html || '';
        iframe.className = 'atelier-flip-frame';
        // Simple apercu : le clavier passe par les emplacements de la surcouche, pas par
        // ce cadre (sinon un Tab de plus s'arrete sur un cadre sans anneau de focus).
        iframe.tabIndex = -1;
        // Squelette gris tant que le serveur n'a pas rendu cette page (jamais un
        // cadre vide) ; retire des qu'un contenu arrive (voir l'effet de "hot-swap").
        if (!html) pageEl.classList.add('is-loading');
        iframe.addEventListener('load', () => flagMissingPhotos(iframe, labelsRef.current.photoMissing));
        iframe.style.width = `${naturalWidthPx}px`;
        iframe.style.height = `${naturalHeightPx}px`;
        clip.appendChild(iframe);
        pageEl.appendChild(clip);

        if (entry.kind === 'cover') iframeMap.cover = iframe;
        else if (entry.kind === 'back-cover') iframeMap.backCover = iframe;
        else iframeMap.interior[entry.pageIndex] = iframe;

        const applyScale = () => {
          const rect = clip.getBoundingClientRect();
          if (rect.width <= 0 || rect.height <= 0) return;
          const scale = Math.min(rect.width / naturalWidthPx, rect.height / naturalHeightPx);
          // Variable CSS sur le cadre, pas sur l'iframe : le double tampon
          // ci-dessous remplace l'iframe par un autre, qui reprend l'echelle.
          clip.style.setProperty('--flip-scale', String(scale));
        };
        applyScale();
        const observer = new ResizeObserver(applyScale);
        observer.observe(clip);
        resizeObservers.push(observer);

        // Hote du portail React (overlay/bouton oeil/actions/legende) — un
        // enfant DE PLUS de la page que page-flip possede, jamais touche par
        // lui (il ne connait que .atelier-flip-page comme unite de
        // decoupage). Voir le commentaire d'en-tete pour le principe complet.
        const portalHost = document.createElement('div');
        portalHost.className = 'atelier-flip-portal-host';
        pageEl.appendChild(portalHost);
        if (entry.kind === 'interior') mounts.push({ kind: 'interior', pageIndex: entry.pageIndex, node: portalHost });
        else mounts.push({ kind: entry.kind, node: portalHost });
      }

      book.appendChild(pageEl);
    });
    container.appendChild(book);
    iframeMapRef.current = iframeMap;
    onMountsReadyRef.current(mounts);

    const logicalWidth = 420;
    const naturalRatio = naturalHeightPx / naturalWidthPx;
    const pf = new PageFlip(book, {
      width: logicalWidth,
      height: Math.round(logicalWidth * naturalRatio),
      size: 'stretch',
      minWidth: 220,
      maxWidth: 1500,
      minHeight: 260,
      maxHeight: 1900,
      showCover: true,
      maxShadowOpacity: 0.4,
      mobileScrollSupport: false,
      flippingTime: 650,
      usePortrait: true,
      startPage: 0,
      autoSize: true,
      drawShadow: true,
      // CORRIGE 2026-10-11 (retour utilisateur : "lors du clic sur 'cliquer
      // pour modifier' sur une page, la page tourne !" / "lorsque je clique
      // sur 'choisir une photo' je suis renvoye vers la page precedente").
      // Par defaut (false), page-flip tourne le feuillet au clic n'IMPORTE
      // OU sur la page, pas seulement sur le coin — ce declenchement est
      // INDEPENDANT du gestionnaire React des boutons (clickEventForward
      // ne fait que laisser les clics ATTEINDRE les boutons, il n'empeche
      // pas la bibliotheque de les interpreter AUSSI comme un geste de
      // tourne-page : un clic sur "Choisir une photo"/"Changer de mise en
      // page" declenchait donc le picker/le panneau ET un feuillet tourne
      // en meme temps, selon la moitie de page cliquee). true : seul le
      // coin replie (showPageCorners, deja actif) ou le glisser-depose du
      // coin declenchent un feuillet — les boutons redeviennent de simples
      // boutons.
      disableFlipByClick: true,
      // CORRIGE 2026-10-11 (retour utilisateur : "les effets sur les bords
      // lorsqu'on passe la souris pour tourner la page [...] m'empechent de
      // modifier la page courante") — showPageCorners (true par defaut)
      // replie visuellement le coin de la page au survol, une couche que
      // page-flip dessine PAR-DESSUS le contenu (pas juste un curseur) :
      // meme coin ou vivent souvent les boutons/emplacements a cliquer,
      // elle interceptait le survol/clic avant qu'il n'atteigne le vrai
      // bouton en dessous. disableFlipByClick (juste au-dessus) empeche
      // deja le feuillet de tourner au clic ; ceci retire en plus l'effet
      // visuel qui genait l'interaction, pas seulement son resultat.
      showPageCorners: false,
      // CORRIGE 2026-10-11, round 2 (retour utilisateur : "quand je clique
      // sur 'ajouter un souvenir' la page se tourne... il faut enlever cet
      // effet, il marche pas bien"). disableFlipByClick n'empeche que le
      // flip declenche par un CLIC pur ; page-flip ecoute aussi
      // mousedown/mousemove/mouseup pour un flip par GLISSEMENT (swipe,
      // useMouseEvents, actif par defaut) — un clic sur un bouton qui ouvre
      // quelque chose (ici l'editeur de texte en ligne) bouge rarement la
      // souris de façon parfaitement immobile entre l'appui et le
      // relachement, et ce leger mouvement suffisait a faire interpreter le
      // clic comme un debut de glissement de page. Plutot que de chasser
      // chaque emplacement un par un, on retire le geste souris en entier :
      // la navigation reste entiere via les fleches/la pellicule de pages
      // (jamais dependante de ce geste), seul le "tourner en glissant/
      // cliquant directement sur le livre" disparait.
      useMouseEvents: false
    });
    pf.loadFromHTML(book.querySelectorAll('.atelier-flip-page'));

    // TAILLE DU LIVRE = TAILLE DE SON CONTENEUR (correctif 10, 2026-10-16). page-flip ne recalcule la taille
    // des pages que sur l'evenement `resize` de la FENETRE : quand c'est le conteneur qui change sans que la
    // fenetre bouge (Agrandir / Reduire, panneau qui se range, bandeau qui apparait), les pages gardaient
    // l'ancienne taille — plus petites que la zone apres Agrandir, plus GRANDES apres Reduire (le livre
    // passait alors sur la barre de navigation). Un ResizeObserver sur le conteneur relance le calcul, une
    // fois par image (rAF), uniquement si la taille a vraiment change.
    let lastW = 0;
    let lastH = 0;
    let frame = 0;
    const refit = () => {
      frame = 0;
      const { width, height } = container.getBoundingClientRect();
      if (Math.round(width) === lastW && Math.round(height) === lastH) return;
      lastW = Math.round(width);
      lastH = Math.round(height);
      try { pf.update(); } catch (_error) { /* livre detruit entre-temps */ }
    };
    const sizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => {
      if (!frame) frame = requestAnimationFrame(refit);
    });
    if (sizeObserver) sizeObserver.observe(container);

    // UNIQUEMENT 'flip' (un feuillet reellement tourne, par l'utilisateur OU
    // par turnToPage programmatique) — jamais 'changeOrientation' (se
    // declenche a chaque redimensionnement/recalcul de mise en page, sans
    // qu'aucune page n'ait change) ni un appel initial manuel juste apres la
    // construction (page-flip demarre TOUJOURS a startPage:0, remonter ce 0
    // ecraserait systematiquement une navigation EXTERNE deja en cours —
    // lien profond ?page=, pellicule... — avant meme que l'effet de
    // synchronisation ait eu la main). Contrairement a l'Apercu final
    // (lecture seule, aucune source de verite concurrente), l'atelier a deja
    // viewIndex/selectedSide comme source de verite : page-flip ne doit
    // remonter que les VRAIS feuillets, jamais un etat de demarrage ou un
    // simple recalcul d'orientation. Bug trouve et corrige le 2026-10-11 :
    // sans ce correctif, le lien profond ?page= revenait systematiquement a
    // la couverture.
    const reportState = () => {
      onFlipChangeRef.current({ index: pf.getCurrentPageIndex(), orientation: pf.getOrientation(), pageCount: entries.length });
    };
    pf.on('flip', reportState);

    // Delegation unique sur le conteneur (jamais une ecoute par page — voir
    // le prototype) : "cliquer une page du double-page pour en faire celle
    // qu'on modifie". Les emplacements de AtelierPageOverlay/
    // CoverPhotoOverlay stoppent deja la propagation de LEURS propres clics
    // (inchange, memes composants qu'avant) : seuls les clics qui NE tombent
    // PAS sur un emplacement remontent jusqu'ici.
    const onContainerClick = (event) => {
      const pageEl = event.target.closest('[data-kind="interior"]');
      if (!pageEl) return;
      onPageClickRef.current(Number(pageEl.dataset.pageIndex));
    };
    container.addEventListener('click', onContainerClick);

    if (apiRef) {
      apiRef.current = {
        turnToPage: (n) => pf.turnToPage(n),
        getCurrentIndex: () => pf.getCurrentPageIndex(),
        pageCount: entries.length
      };
    }

    return () => {
      container.removeEventListener('click', onContainerClick);
      if (sizeObserver) sizeObserver.disconnect();
      if (frame) cancelAnimationFrame(frame);
      resizeObservers.forEach((observer) => observer.disconnect());
      try { pf.destroy(); } catch (_err) { /* deja detruit */ }
      if (apiRef) apiRef.current = null;
      onMountsReadyRef.current([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPages, printFormat, labels.insideCover, labels.insideBackCover]);

  // Met a jour le CONTENU sans jamais recreer PageFlip (une page qui vient
  // d'etre sauvegardee doit changer a l'ecran sans perdre la lecture en
  // cours) — meme principe que le prototype (placedByPage), applique ici a
  // iframe.srcdoc plutot qu'un style.backgroundImage.
  useEffect(() => {
    const map = iframeMapRef.current;
    const reveal = (iframe) => { const pageEl = iframe.closest('.atelier-flip-page'); if (pageEl) pageEl.classList.remove('is-loading'); };

    // DOUBLE TAMPON : changer `srcdoc` d'un iframe qui affiche deja une page la
    // fait repartir de blanc le temps que le nouveau document et ses images se
    // chargent — un flash a chaque modification. On charge donc le nouveau
    // contenu dans un SECOND iframe, cache, posé au meme endroit, et on ne
    // remplace l'ancien que quand le nouveau est pret. Le premier contenu d'une
    // page (rien a l'ecran) se pose directement.
    const apply = (iframe, html, assign) => {
      if (!iframe || html == null) return;
      if (iframe.srcdoc === html) { reveal(iframe); return; }
      if (!iframe.srcdoc) {
        iframe.srcdoc = html;
        reveal(iframe);
        return;
      }
      const clip = iframe.parentElement;
      if (!clip) { iframe.srcdoc = html; return; }
      const next = iframe.cloneNode(false);
      next.srcdoc = html;
      next.style.position = 'absolute';
      next.style.visibility = 'hidden';
      next.addEventListener('load', () => {
        next.style.position = '';
        next.style.visibility = '';
        clip.querySelectorAll('iframe').forEach((other) => { if (other !== next) other.remove(); });
        flagMissingPhotos(next, labelsRef.current.photoMissing);
      }, { once: true });
      clip.appendChild(next);
      assign(next);
      reveal(next);
    };

    apply(map.cover, contentRef.current.coverHtml, (next) => { map.cover = next; });
    apply(map.backCover, contentRef.current.backCoverHtml, (next) => { map.backCover = next; });
    Object.entries(contentRef.current.pagePreviewCache || {}).forEach(([pageIndex, html]) => {
      apply(map.interior[pageIndex], html, (next) => { map.interior[pageIndex] = next; });
    });
    // Se declenche a chaque evolution du cache (voir BookAtelierLuxe.js) —
    // `contentRef` lui-meme ne change jamais d'identite (ajoute pour
    // satisfaire exhaustive-deps, sans effet sur la frequence reelle).
  }, [contentRef, contentRef.current.coverHtml, contentRef.current.backCoverHtml, contentRef.current.pagePreviewCache]);

  // Reflete "quelle page modifie-t-on" sur le DOM (bordure or / grisaille,
  // voir .atelier-page-pane.is-selected/.is-inactive dans le CSS) — sans
  // reconstruire le livre, un simple classList.toggle par page interieure.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.querySelectorAll('[data-kind="interior"]').forEach((el) => {
      const isSelected = Number(el.dataset.pageIndex) === currentPageIndex;
      el.classList.toggle('is-selected', isSelected);
      el.classList.toggle('is-inactive', !isSelected);
    });
  }, [currentPageIndex, totalPages]);

  return (
    <div className="atelier-flip-stagewrap">
      <div ref={containerRef} className="atelier-flip-stage" />
    </div>
  );
}

function AtelierFlipBookView({
  totalPages,
  coverHtml,
  backCoverHtml,
  pagePreviewCache,
  loading,
  previewFailed,
  onRetryPreview,
  viewKind,
  leftPageIndex,
  rightPageIndex,
  currentPageIndex,
  selectedSide,
  onSelectSide,
  onNavigate,
  onPrevious,
  onNext,
  canGoPrevious,
  canGoNext,
  navLabel,
  onGoToCover,
  onGoToBackCover,
  overlay,
  // Cadre pointille VISUEL pose sur la page jumelle d une double page (voir BookAtelierLuxe : twinOverlay).
  twinOverlay,
  twinPageIndex,
  pageActions,
  printFormat,
  onAssignCoverPhoto,
  // Cadrage de la couverture/4e (phase 4a) : voir CoverPhotoOverlay.
  selectedCoverFace,
  onSelectCoverFace,
  coverFraming,
  coverPhotoItem,
  onCoverFramingChange,
  coverPicker,
  // Edition directe du titre / sous-titre / date sur la couverture : { values, onChange }.
  coverText,
  coverHasPhoto,
  onOpenCoverPhotoPicker,
  frontHasPhotoSlot,
  backHasPhotoSlot,
  selectedSidebarItem,
  onOpenLayoutDrawer,
  // Une photo etalee sur DOUBLE PAGE est visible dans cette vue : explication (i) + consigne.
  spreadPhoto,
  // Une page de la vue a une photo en basse resolution ; originaux pour le zoom (apercu plein ecran).
  lowResolution,
  photoUpgrades
}) {
  const { t } = useTranslation('atelier');
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  // Apercu plein ecran sur telephone : UNE page a la fois (une double page y serait minuscule).
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 700);
  const [viewerSide, setViewerSide] = useState('left');
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 700);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => { if (isFullscreenOpen) setViewerSide(selectedSide === 'right' ? 'right' : 'left'); }, [isFullscreenOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  const [portalMounts, setPortalMounts] = useState([]);
  const flipApiRef = useRef(null);

  // Pont lu par AtelierFlipStage (jamais l'etat directement depuis son
  // effet de montage, qui ne se rejoue pas a chaque rendu) — voir son
  // en-tete. Mis a jour a CHAQUE rendu, inconditionnellement.
  const contentRef = useRef({ coverHtml, backCoverHtml, pagePreviewCache });
  contentRef.current = { coverHtml, backCoverHtml, pagePreviewCache };

  const onNavigateRef = useRef(onNavigate);
  onNavigateRef.current = onNavigate;
  const leftPageIndexRef = useRef(leftPageIndex);
  leftPageIndexRef.current = leftPageIndex;
  const rightPageIndexRef = useRef(rightPageIndex);
  rightPageIndexRef.current = rightPageIndex;
  const onSelectSideRef = useRef(onSelectSide);
  onSelectSideRef.current = onSelectSide;

  const handlePageClick = useCallback((pageIndex) => {
    if (pageIndex === leftPageIndexRef.current) onSelectSideRef.current('left');
    else if (pageIndex === rightPageIndexRef.current) onSelectSideRef.current('right');
  }, []);

  // Feuillet tourne par l'utilisateur (glisser ou clic sur le bord du livre,
  // geste natif de page-flip) -> meme forme que la cible attendue par
  // goToFilmstripTarget (BookAtelierLuxe.js), reutilisee telle quelle par
  // l'appelant plutot que de dupliquer sa logique de conversion ici.
  const handleFlipChange = useCallback(({ index, pageCount }) => {
    if (index <= FLIP_COVER_INDEX) { onNavigateRef.current('cover'); return; }
    if (pageCount > 0 && index >= pageCount - 1) { onNavigateRef.current('back-cover'); return; }
    const contentPageIndex = contentPageFromFlipIndex(index, totalPages);
    // Deja sur ce double-page (l'une de ses deux pages est affichee) : on ne change rien — sinon la page de DROITE
    // choisie (ex. apres un ajout de pages) repassait a la page de gauche quand le livre finissait de tourner.
    if (contentPageIndex != null && (contentPageIndex === leftPageIndexRef.current || contentPageIndex === rightPageIndexRef.current)) return;
    if (contentPageIndex != null) onNavigateRef.current(contentPageIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPages]);

  const handleMountsReady = useCallback((mounts) => setPortalMounts(mounts), []);

  // Navigation EXTERNE (fleches, pellicule, lien profond ?page=, boutons
  // "aller a la couverture/4e") -> cible dans l'espace d'index de page-flip,
  // appliquee UNIQUEMENT si elle differe de ce que page-flip affiche deja
  // (sinon chaque feuillet tourne par l'utilisateur se retournerait lui-meme
  // via cette meme synchronisation — boucle sans fin).
  const targetFlipIndex = viewKind === 'cover' ? FLIP_COVER_INDEX
    : viewKind === 'back-cover' ? flipBackCoverIndex(totalPages)
      : flipIndexFromContentPage(leftPageIndex != null ? leftPageIndex : rightPageIndex);
  useEffect(() => {
    const api = flipApiRef.current;
    if (!api || targetFlipIndex == null) return;
    if (api.getCurrentIndex() === targetFlipIndex) return;
    api.turnToPage(targetFlipIndex);
    // portalMounts : change quand le livre est RECONSTRUIT (pages ajoutees/retirees) — le nouveau livre repart de
    // la couverture, il faut le replacer sur la page courante.
  }, [targetFlipIndex, portalMounts]);

  const labels = {
    insideCover: t('bookView.insideCoverLabel'),
    insideBackCover: t('bookView.insideBackCoverLabel'),
    photoMissing: t('store.states.photoMissing')
  };

  const hasContentToExpand = viewKind === 'spread' ? Boolean(
    (leftPageIndex != null && pagePreviewCache[leftPageIndex]) || (rightPageIndex != null && pagePreviewCache[rightPageIndex])
  ) : Boolean(viewKind === 'cover' ? coverHtml : backCoverHtml);

  const formatDimensions = FORMAT_DIMENSIONS_MM[printFormat] || FORMAT_DIMENSIONS_MM.standard;
  const pageWidthPx = formatDimensions.widthMm * PX_PER_MM;
  const pageHeightPx = formatDimensions.heightMm * PX_PER_MM;
  const isSpreadWithBothPages = viewKind === 'spread' && leftPageIndex != null && rightPageIndex != null;
  const singleHtml = viewKind === 'cover' ? coverHtml : viewKind === 'back-cover' ? backCoverHtml : null;
  const leftHtml = leftPageIndex != null ? pagePreviewCache[leftPageIndex] : null;
  const rightHtml = rightPageIndex != null ? pagePreviewCache[rightPageIndex] : null;

  // Apercu plein ecran : les pages de la vue (une seule pour la couverture / 4e), avec le cote du
  // pli pour la superposition « marges et pli ».
  const pageMetrics = getPageMetrics(printFormat);
  const singleViewer = narrow && viewKind === 'spread';
  const shownIndex = singleViewer
    ? (viewerSide === 'right' ? (rightPageIndex != null ? rightPageIndex : leftPageIndex) : (leftPageIndex != null ? leftPageIndex : rightPageIndex))
    : null;
  const viewerPages = singleViewer && shownIndex != null
    ? [{
      key: `p${shownIndex}`,
      html: shownIndex === leftPageIndex ? leftHtml : rightHtml,
      label: t('bookView.pageLabel', { number: shownIndex + 1 }),
      fold: shownIndex === leftPageIndex ? 'right' : 'left'
    }]
    : viewKind === 'spread'
    ? [
      leftPageIndex != null && { key: `p${leftPageIndex}`, html: leftHtml, label: t('bookView.pageLabel', { number: leftPageIndex + 1 }), fold: 'right' },
      rightPageIndex != null && { key: `p${rightPageIndex}`, html: rightHtml, label: t('bookView.pageLabel', { number: rightPageIndex + 1 }), fold: 'left' }
    ].filter(Boolean)
    : [{ key: viewKind, html: singleHtml, label: viewKind === 'cover' ? t('bookView.coverLabel') : t('bookView.backCoverLabel'), fold: null }];
  const viewerTitle = viewKind !== 'spread'
    ? navLabel
    : (singleViewer ? t('bookView.pageLabel', { number: shownIndex + 1 }) : isSpreadWithBothPages
      ? t('pageViewer.pages', { ns: 'common', left: leftPageIndex + 1, right: rightPageIndex + 1 })
      : navLabel);
  const viewerCounter = viewKind !== 'spread'
    ? navLabel
    : (singleViewer ? `${shownIndex + 1} / ${totalPages}` : isSpreadWithBothPages
      ? `${leftPageIndex + 1}–${rightPageIndex + 1} / ${totalPages}`
      : `${(leftPageIndex != null ? leftPageIndex : rightPageIndex) + 1} / ${totalPages}`);

  // Telephone : page par page (gauche puis droite, puis la double page suivante).
  const canStepWithin = (direction) => singleViewer && (
    (direction > 0 && viewerSide === 'left' && leftPageIndex != null && rightPageIndex != null)
    || (direction < 0 && viewerSide === 'right' && leftPageIndex != null && rightPageIndex != null)
  );
  const viewerNext = () => {
    if (canStepWithin(1)) { setViewerSide('right'); return; }
    setViewerSide('left');
    onNext();
  };
  const viewerPrev = () => {
    if (canStepWithin(-1)) { setViewerSide('left'); return; }
    setViewerSide('right');
    onPrevious();
  };

  return (
    <div className="atelier-book-view">
      {(viewKind === 'cover' || viewKind === 'back-cover') && (
      <div className={`atelier-cover-bar-wrap ${viewKind === 'cover' ? 'is-front' : 'is-back'}`} style={{ '--flip-spread-ratio': String((2 * pageWidthPx) / pageHeightPx) }}>
        <div className="atelier-cover-bar" role="toolbar" aria-label={t('bookView.coverBar.label')}>
          {onOpenLayoutDrawer && (
            <button type="button" className="atelier-cover-bar-btn" onClick={onOpenLayoutDrawer}>
              {t('bookView.coverBar.style')}
            </button>
          )}
          {onOpenCoverPhotoPicker && (viewKind === 'cover' ? frontHasPhotoSlot : backHasPhotoSlot) && (
            <button
              type="button"
              className="atelier-cover-bar-btn"
              onClick={() => onOpenCoverPhotoPicker(viewKind === 'cover' ? 'front' : 'back')}
            >
              {t('bookView.coverBar.photo')}
            </button>
          )}
          {viewKind === 'cover' && coverText && (
            <button
              type="button"
              className="atelier-cover-bar-btn"
              onClick={() => window.dispatchEvent(new CustomEvent('atelier:cover-edit-text'))}
            >
              {t('bookView.coverBar.texts')}
            </button>
          )}
        </div>
      </div>
      )}
      <div className="atelier-book-stage atelier-flip-book-stage">
        {previewFailed ? (
          <p className="atelier-hint atelier-book-loading is-error" role="status">
            {t('store.states.previewUnavailable')}{' '}
            <button type="button" className="atelier-book-loading-retry" onClick={onRetryPreview}>{t('store.states.retry')}</button>
          </p>
        ) : (loading && <div className="atelier-book-loader"><BookipixLoader size="md" label={t('bookView.loadingPage')} /></div>)}

        <AtelierFlipStage
          totalPages={totalPages}
          printFormat={printFormat}
          currentPageIndex={currentPageIndex}
          labels={labels}
          contentRef={contentRef}
          onMountsReady={handleMountsReady}
          onFlipChange={handleFlipChange}
          apiRef={flipApiRef}
          onPageClick={handlePageClick}
        />

        {portalMounts.map((mount) => {
          if (mount.kind === 'cover' || mount.kind === 'back-cover') {
            const face = mount.kind === 'cover' ? 'front' : 'back';
            const html = mount.kind === 'cover' ? coverHtml : backCoverHtml;
            return createPortal(
              <>
                {face === 'front' && isMultiPhotoCover(html) && onOpenCoverPhotoPicker && (
                  <CoverMultiPhotoOverlay
                    html={html}
                    activeSlot={coverPicker && coverPicker.face === 'front' ? coverPicker.slot : null}
                    onPickSlot={(slot) => onOpenCoverPhotoPicker('front', slot)}
                  />
                )}
                {!(face === 'front' && isMultiPhotoCover(html)) && <CoverPhotoOverlay
                  onAssign={onAssignCoverPhoto ? (itemId) => onAssignCoverPhoto(face, itemId) : null}
                  selectedSidebarItem={selectedSidebarItem}
                  face={face}
                  selected={selectedCoverFace === face}
                  onSelect={onSelectCoverFace ? (next) => onSelectCoverFace(next === null ? null : face) : null}
                  photoItem={coverPhotoItem}
                  framing={selectedCoverFace === face ? coverFraming : null}
                  onFramingChange={onCoverFramingChange}
                  picker={coverPicker && coverPicker.face === face ? coverPicker : null}
                  printFormat={printFormat}
                  hasPhoto={coverHasPhoto}
                  onOpenPicker={onOpenCoverPhotoPicker ? () => onOpenCoverPhotoPicker(face) : null}
                  onRemove={onAssignCoverPhoto ? () => onAssignCoverPhoto(face, null) : null}
                  hasPhotoSlot={face === 'front' ? frontHasPhotoSlot : backHasPhotoSlot}
                  onOpenLayout={onOpenLayoutDrawer}
                />}
                {face === 'front' && coverText && html && (
                  <CoverTextOverlay html={html} values={coverText.values} onChange={coverText.onChange} />
                )}
              </>,
              mount.node,
              mount.kind
            );
          }

          const { pageIndex } = mount;
          const isSelected = pageIndex === currentPageIndex;
          return createPortal(
            <>
              {isSelected && overlay}
              {!isSelected && twinOverlay && pageIndex === twinPageIndex && twinOverlay}
              {isSelected && pageActions}
              {!isSelected && <span className="atelier-page-pane-inactive-hint">{t('bookView.clickToEdit')}</span>}
              <span className="atelier-page-pane-label">{t('bookView.pageLabel', { number: pageIndex + 1 })}</span>
            </>,
            mount.node,
            `interior-${pageIndex}`
          );
        })}
      </div>

      <div className="atelier-book-nav">
        <div className="atelier-book-nav-side" aria-hidden="true" />
        <div className="atelier-book-nav-controls">
          {onGoToCover && (
            <button
              type="button"
              className="atelier-book-nav-jump"
              onClick={onGoToCover}
              disabled={!canGoPrevious}
              title={t('bookView.goToCover')}
              aria-label={t('bookView.goToCover')}
            >
              ⇤
            </button>
          )}
          <button
            type="button"
            className="atelier-book-nav-arrow"
            onClick={onPrevious}
            disabled={!canGoPrevious}
            aria-label={t('bookView.previousPage')}
          >
            ‹
          </button>
          <span className="atelier-book-nav-label">
            {navLabel}{totalPages ? ` / ${totalPages}` : ''}
          </span>
          <button
            type="button"
            className="atelier-book-nav-arrow"
            onClick={onNext}
            disabled={!canGoNext}
            aria-label={t('bookView.nextPage')}
          >
            ›
          </button>
          {onGoToBackCover && (
            <button
              type="button"
              className="atelier-book-nav-jump"
              onClick={onGoToBackCover}
              disabled={!canGoNext}
              title={t('bookView.goToBackCover')}
              aria-label={t('bookView.goToBackCover')}
            >
              ⇥
            </button>
          )}
        </div>
        {/* Plus de bouton "Previsualiser" ici : l'oeil pose sur chaque page et sur
            les couvertures fait la meme chose (retour utilisateur). Les deux zones
            laterales de meme largeur gardent les fleches CENTREES sous le livre. */}
        <div className="atelier-book-nav-side atelier-book-nav-side-end">
          {hasContentToExpand && (
            <button
              type="button"
              className="atelier-book-nav-eye"
              onClick={() => setIsFullscreenOpen(true)}
              title={t('bookView.previewTitle')}
              aria-label={t('bookView.preview')}
            >
              <EyeIcon />
            </button>
          )}
        </div>
      </div>

      {/* Photo sur double page : la consigne reste TOUJOURS affichee dans l'atelier
          (eviter un visage pres de la reliure) ; l'explication du leger doublon central
          s'ouvre au (i). */}
      {spreadPhoto && (
        <div className="atelier-spread-note">
          <SpreadPhotoInfo placement="top" layflat={gutterSafetyMmOf(printFormat) === 0} />
        </div>
      )}

      {/* APERCU PLEIN ECRAN (2026-10-12) : composant partage, voir common/PagePreviewViewer.js. */}
      {isFullscreenOpen && hasContentToExpand && (
        <PagePreviewViewer
          title={viewerTitle}
          pages={viewerPages}
          pageWidthPx={pageWidthPx}
          pageHeightPx={pageHeightPx}
          spineGapPx={SPREAD_GAP_PX}
          counter={viewerCounter}
          canPrev={canGoPrevious || canStepWithin(-1)}
          canNext={canGoNext || canStepWithin(1)}
          onPrev={viewerPrev}
          onNext={viewerNext}
          onFirst={onGoToCover}
          onLast={onGoToBackCover}
          onClose={() => setIsFullscreenOpen(false)}
          pageWidthMm={formatDimensions.widthMm}
          marginMm={pageMetrics.paddingMm}
          foldMm={gutterSafetyMmOf(printFormat)}
          lowRes={Boolean(lowResolution)}
          photoUpgrades={photoUpgrades}
          spreadPhoto={spreadPhoto}
        />
      )}
    </div>
  );
}

export default AtelierFlipBookView;
